import { useState } from "react";
import {
  Square, Circle, Triangle, Star, MessageSquare, MoveRight, Minus, CornerDownRight, Type, Image as ImageIcon,
  Film, Copy, Trash2, Undo2, Redo2, Group, Ungroup, BringToFront, SendToBack, ArrowUp, ArrowDown,
  AlignStartVertical, AlignCenterVertical, AlignEndVertical, AlignStartHorizontal, AlignCenterHorizontal, AlignEndHorizontal,
  Grid3x3, RotateCw, RotateCcw, Download, Sparkles, Play,
} from "lucide-react";

const SHAPE_BTNS = [
  ["rect", Square, "Rectangle"], ["roundrect", Square, "Rounded rectangle"], ["ellipse", Circle, "Circle / ellipse"],
  ["triangle", Triangle, "Triangle"], ["star", Star, "Star"], ["bubble", MessageSquare, "Speech bubble"],
  ["arrowshape", MoveRight, "Block arrow"], ["line", Minus, "Line"], ["elbow", CornerDownRight, "Elbow connector"],
  ["text", Type, "Text box"], ["image", ImageIcon, "Image"], ["video", Film, "Video"],
];

const TBtn = ({ testid, title, onClick, active, disabled, children }) => (
  <button data-testid={testid} title={title} onClick={onClick} disabled={disabled}
    className={`h-8 min-w-8 px-2 rounded-md inline-flex items-center justify-center gap-1 text-xs transition-colors ${active ? "bg-[#22D3EE] text-[#04121f]" : "text-slate-200 hover:bg-white/10"} ${disabled ? "opacity-35 cursor-not-allowed" : ""}`}>
    {children}
  </button>
);

const Sep = () => <span className="w-px h-6 bg-white/15 mx-0.5" />;

export function ColorField({ testid, label, value, onChange, swatches, allowNone }) {
  const [open, setOpen] = useState(false);
  const [hex, setHex] = useState(value || "#000000");
  const commit = (v) => {
    const t = v.trim();
    const full = t.startsWith("#") ? t : `#${t}`;
    if (/^#[0-9a-fA-F]{6}$/.test(full)) { onChange(full.toUpperCase()); setOpen(false); }
  };
  return (
    <div className="relative">
      <button data-testid={testid} onClick={() => { setHex(value || "#000000"); setOpen((o) => !o); }}
        className="h-8 px-2 rounded-md inline-flex items-center gap-1.5 text-xs text-slate-200 hover:bg-white/10">
        <span className="w-4 h-4 rounded border border-white/40" style={{ background: value === "none" ? "transparent" : value, backgroundImage: value === "none" ? "linear-gradient(45deg,#888 25%,transparent 25%,transparent 75%,#888 75%)" : undefined }} />
        {label}
      </button>
      {open && (
        <div data-testid={`${testid}-panel`} className="absolute z-40 top-9 left-0 w-56 rounded-xl bg-slate-900 border border-white/15 p-3 shadow-2xl">
          <div className="grid grid-cols-8 gap-1.5">
            {swatches.map((s) => (
              <button key={s.hex} data-testid={`${testid}-sw-${s.hex.replace("#", "")}`} title={`${s.name} ${s.hex}`} onClick={() => { onChange(s.hex); setOpen(false); }}
                className="w-5 h-5 rounded border border-white/30 hover:scale-110 transition-transform" style={{ background: s.hex }} />
            ))}
          </div>
          <p className="text-[10px] uppercase tracking-widest text-slate-400 mt-3 mb-1">Hex code</p>
          <div className="flex gap-1.5">
            <input data-testid={`${testid}-hex`} value={hex} onChange={(e) => setHex(e.target.value)} onKeyDown={(e) => e.key === "Enter" && commit(hex)}
              placeholder="#22D3EE" className="flex-1 h-8 rounded-md bg-slate-800 border border-slate-600 px-2 font-mono text-xs text-white outline-none focus:border-[#22D3EE]" />
            <button data-testid={`${testid}-hex-apply`} onClick={() => commit(hex)} className="h-8 px-2 rounded-md bg-[#22D3EE] text-[#04121f] text-xs font-medium">Apply</button>
          </div>
          {allowNone && <button data-testid={`${testid}-none`} onClick={() => { onChange("none"); setOpen(false); }} className="mt-2 text-[11px] text-slate-300 hover:text-white underline">No fill (transparent)</button>}
        </div>
      )}
    </div>
  );
}

export default function DrawToolbar({ doc, config, sel, selObjs, act }) {
  const one = selObjs[0];
  const isLine = one && (one.type === "line" || one.type === "elbow");
  const isText = one && one.type === "text";
  const [panel, setPanel] = useState(null);
  const swatches = config.swatches || [];

  return (
    <div className="bg-slate-950/90 border-b border-white/10">
      {/* Row 1 — insert + canvas + history */}
      <div className="flex items-center gap-0.5 flex-wrap px-2 py-1.5">
        {SHAPE_BTNS.map(([t, Icon, title]) => (
          <TBtn key={t} testid={`draw-add-${t}`} title={`Add ${title}`} onClick={() => act.add(t)}>
            <Icon className={`w-4 h-4 ${t === "roundrect" ? "rounded-md" : ""}`} strokeWidth={t === "roundrect" ? 3 : 2} />
          </TBtn>
        ))}
        <Sep />
        <ColorField testid="draw-canvas-bg" label="Background" value={(doc.canvas || {}).bg} onChange={(v) => act.setCanvas({ bg: v })} swatches={swatches} />
        <TBtn testid="draw-grid-toggle" title="Snap to grid" active={!!(doc.canvas || {}).grid} onClick={() => act.setCanvas({ grid: !(doc.canvas || {}).grid })}><Grid3x3 className="w-4 h-4" /> Grid</TBtn>
        <Sep />
        <TBtn testid="draw-undo" title="Undo" onClick={act.undo} disabled={!act.canUndo}><Undo2 className="w-4 h-4" /></TBtn>
        <TBtn testid="draw-redo" title="Redo" onClick={act.redo} disabled={!act.canRedo}><Redo2 className="w-4 h-4" /></TBtn>
        <Sep />
        <TBtn testid="draw-play-anim" title="Preview animations" onClick={act.play}><Play className="w-4 h-4" /> Preview</TBtn>
        <TBtn testid="draw-export-png" title="Download as PNG" onClick={act.exportPng}><Download className="w-4 h-4" /> PNG</TBtn>
        <div className="ml-auto flex items-center gap-2">
          <input data-testid="draw-name-input" value={doc.name || ""} onChange={(e) => act.setName(e.target.value)} placeholder="Untitled design"
            className="h-8 w-40 rounded-md bg-slate-800/80 border border-slate-600 px-2 text-xs text-white placeholder:text-slate-400 outline-none focus:border-[#22D3EE]" />
        </div>
      </div>

      {/* Row 2 — selection properties */}
      <div className="flex items-center gap-0.5 flex-wrap px-2 py-1.5 border-t border-white/10 min-h-[44px]">
        {!one ? (
          <p data-testid="draw-no-selection" className="text-xs text-slate-400 px-1">Click a shape on the canvas to change its colors, size, order and grouping.</p>
        ) : (
          <>
            {!isLine && <ColorField testid="draw-fill" label="Fill" value={one.fill} onChange={(v) => act.update({ fill: v })} swatches={swatches} allowNone />}
            <ColorField testid="draw-stroke" label="Line color" value={one.stroke} onChange={(v) => act.update({ stroke: v })} swatches={swatches} />
            <div className="relative">
              <TBtn testid="draw-stroke-width" title="Line thickness" onClick={() => setPanel(panel === "sw" ? null : "sw")}>Thickness {one.strokeWidth || 0}</TBtn>
              {panel === "sw" && (
                <div className="absolute z-40 top-9 left-0 rounded-xl bg-slate-900 border border-white/15 p-2 shadow-2xl flex gap-1">
                  {(config.strokeWidths || []).map((w) => (
                    <button key={w} data-testid={`draw-sw-${w}`} onClick={() => { act.update({ strokeWidth: w }); setPanel(null); }}
                      className={`h-7 px-2 rounded text-xs ${(one.strokeWidth || 0) === w ? "bg-[#22D3EE] text-[#04121f]" : "text-slate-200 hover:bg-white/10"}`}>{w}</button>
                  ))}
                </div>
              )}
            </div>
            <div className="relative">
              <TBtn testid="draw-dash" title="Line style" onClick={() => setPanel(panel === "dash" ? null : "dash")}>{(one.dash || "solid")}</TBtn>
              {panel === "dash" && (
                <div className="absolute z-40 top-9 left-0 rounded-xl bg-slate-900 border border-white/15 p-2 shadow-2xl flex gap-1">
                  {(config.dashes || []).map((d) => (
                    <button key={d.id} data-testid={`draw-dash-${d.id}`} onClick={() => { act.update({ dash: d.id }); setPanel(null); }}
                      className={`h-7 px-2 rounded text-xs ${(one.dash || "solid") === d.id ? "bg-[#22D3EE] text-[#04121f]" : "text-slate-200 hover:bg-white/10"}`}>{d.name}</button>
                  ))}
                </div>
              )}
            </div>
            {isLine && (
              <>
                <TBtn testid="draw-arrow-end" title="Arrowhead at the end" active={!!one.arrowEnd} onClick={() => act.update({ arrowEnd: !one.arrowEnd })}>Arrow →</TBtn>
                <TBtn testid="draw-arrow-start" title="Arrowhead at the start" active={!!one.arrowStart} onClick={() => act.update({ arrowStart: !one.arrowStart })}>← Arrow</TBtn>
              </>
            )}
            <Sep />
            <span className="text-[10px] uppercase tracking-widest text-slate-500 pl-1">Size</span>
            <label className="inline-flex items-center gap-1 text-xs text-slate-300">W
              <input data-testid="draw-w-input" type="number" min={8} value={Math.round(one.w)} onChange={(e) => act.update({ w: Math.max(8, Number(e.target.value) || 8) })}
                className="w-16 h-8 rounded-md bg-slate-800 border border-slate-600 px-1.5 font-mono-data text-xs text-white outline-none focus:border-[#22D3EE]" />
            </label>
            <label className="inline-flex items-center gap-1 text-xs text-slate-300">H
              <input data-testid="draw-h-input" type="number" min={8} value={Math.round(one.h)} onChange={(e) => act.update({ h: Math.max(8, Number(e.target.value) || 8) })}
                className="w-16 h-8 rounded-md bg-slate-800 border border-slate-600 px-1.5 font-mono-data text-xs text-white outline-none focus:border-[#22D3EE]" />
            </label>
            <label className="inline-flex items-center gap-1 text-xs text-slate-300">X
              <input data-testid="draw-x-input" type="number" value={Math.round(one.x)} onChange={(e) => act.update({ x: Number(e.target.value) || 0 })}
                className="w-16 h-8 rounded-md bg-slate-800 border border-slate-600 px-1.5 font-mono-data text-xs text-white outline-none focus:border-[#22D3EE]" />
            </label>
            <label className="inline-flex items-center gap-1 text-xs text-slate-300">Y
              <input data-testid="draw-y-input" type="number" value={Math.round(one.y)} onChange={(e) => act.update({ y: Number(e.target.value) || 0 })}
                className="w-16 h-8 rounded-md bg-slate-800 border border-slate-600 px-1.5 font-mono-data text-xs text-white outline-none focus:border-[#22D3EE]" />
            </label>
            <Sep />
            <div className="relative">
              <TBtn testid="draw-opacity" title="Opacity" onClick={() => setPanel(panel === "op" ? null : "op")}>Opacity {Math.round((one.opacity ?? 1) * 100)}%</TBtn>
              {panel === "op" && (
                <div className="absolute z-40 top-9 left-0 rounded-xl bg-slate-900 border border-white/15 p-2 shadow-2xl flex gap-1">
                  {[1, 0.75, 0.5, 0.25].map((v) => (
                    <button key={v} data-testid={`draw-op-${Math.round(v * 100)}`} onClick={() => { act.update({ opacity: v }); setPanel(null); }}
                      className={`h-7 px-2 rounded text-xs ${(one.opacity ?? 1) === v ? "bg-[#22D3EE] text-[#04121f]" : "text-slate-200 hover:bg-white/10"}`}>{v * 100}%</button>
                  ))}
                </div>
              )}
            </div>
            <TBtn testid="draw-rotate-ccw" title="Rotate left 15°" onClick={() => act.rotate(-15)}><RotateCcw className="w-4 h-4" /></TBtn>
            <TBtn testid="draw-rotate-cw" title="Rotate right 15°" onClick={() => act.rotate(15)}><RotateCw className="w-4 h-4" /></TBtn>
            <span data-testid="draw-rot-value" className="text-[11px] text-slate-400 px-1">{Math.round(one.rot || 0)}°</span>
            <Sep />
            <TBtn testid="draw-bring-forward" title="Bring forward" onClick={() => act.arrange("forward")}><ArrowUp className="w-4 h-4" /></TBtn>
            <TBtn testid="draw-send-backward" title="Send backward" onClick={() => act.arrange("backward")}><ArrowDown className="w-4 h-4" /></TBtn>
            <TBtn testid="draw-bring-front" title="Bring to front" onClick={() => act.arrange("front")}><BringToFront className="w-4 h-4" /></TBtn>
            <TBtn testid="draw-send-back" title="Send to back" onClick={() => act.arrange("back")}><SendToBack className="w-4 h-4" /></TBtn>
            <Sep />
            <TBtn testid="draw-group" title="Group selected" onClick={act.group} disabled={sel.length < 2}><Group className="w-4 h-4" /> Group</TBtn>
            <TBtn testid="draw-ungroup" title="Ungroup" onClick={act.ungroup} disabled={!selObjs.some((o) => o.group)}><Ungroup className="w-4 h-4" /> Ungroup</TBtn>
            <Sep />
            <TBtn testid="draw-align-left" title="Align left" onClick={() => act.align("left")}><AlignStartVertical className="w-4 h-4" /></TBtn>
            <TBtn testid="draw-align-centerx" title="Align centers" onClick={() => act.align("centerX")}><AlignCenterVertical className="w-4 h-4" /></TBtn>
            <TBtn testid="draw-align-right" title="Align right" onClick={() => act.align("right")}><AlignEndVertical className="w-4 h-4" /></TBtn>
            <TBtn testid="draw-align-top" title="Align top" onClick={() => act.align("top")}><AlignStartHorizontal className="w-4 h-4" /></TBtn>
            <TBtn testid="draw-align-centery" title="Align middles" onClick={() => act.align("centerY")}><AlignCenterHorizontal className="w-4 h-4" /></TBtn>
            <TBtn testid="draw-align-bottom" title="Align bottom" onClick={() => act.align("bottom")}><AlignEndHorizontal className="w-4 h-4" /></TBtn>
            <Sep />
            <TBtn testid="draw-duplicate" title="Duplicate" onClick={act.duplicate}><Copy className="w-4 h-4" /></TBtn>
            <TBtn testid="draw-delete" title="Delete" onClick={act.remove}><Trash2 className="w-4 h-4" /></TBtn>
            <div className="relative">
              <TBtn testid="draw-anim" title="Animation" onClick={() => setPanel(panel === "an" ? null : "an")}><Sparkles className="w-4 h-4" /> {one.anim || "none"}</TBtn>
              {panel === "an" && (
                <div className="absolute z-40 top-9 right-0 rounded-xl bg-slate-900 border border-white/15 p-2 shadow-2xl flex gap-1">
                  {(config.animations || []).map((a) => (
                    <button key={a.id} data-testid={`draw-anim-${a.id}`} onClick={() => { act.update({ anim: a.id }); setPanel(null); }}
                      className={`h-7 px-2 rounded text-xs whitespace-nowrap ${(one.anim || "none") === a.id ? "bg-[#22D3EE] text-[#04121f]" : "text-slate-200 hover:bg-white/10"}`}>{a.name}</button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Row 3 — type-specific editors */}
      {one && (isText || one.type === "image" || one.type === "video" || !isLine) && (
        <div className="flex items-center gap-2 flex-wrap px-2 py-1.5 border-t border-white/10">
          {!isLine && (
            <input data-testid="draw-text-input" value={one.text || ""} onChange={(e) => act.update({ text: e.target.value })} placeholder={isText ? "Type your text…" : "Label inside the shape (optional)"}
              className="h-8 flex-1 min-w-[160px] rounded-md bg-slate-800/80 border border-slate-600 px-2 text-xs text-white placeholder:text-slate-400 outline-none focus:border-[#22D3EE]" />
          )}
          {!isLine && (
            <>
              <select data-testid="draw-font-family" value={one.fontFamily} onChange={(e) => act.update({ fontFamily: e.target.value })}
                className="h-8 rounded-md bg-slate-800/80 border border-slate-600 px-1.5 text-xs text-white outline-none">
                {(config.fonts || []).map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <select data-testid="draw-font-size" value={one.fontSize} onChange={(e) => act.update({ fontSize: Number(e.target.value) })}
                className="h-8 rounded-md bg-slate-800/80 border border-slate-600 px-1.5 text-xs text-white outline-none">
                {(config.fontSizes || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <TBtn testid="draw-bold" title="Bold" active={!!one.bold} onClick={() => act.update({ bold: !one.bold })}><b>B</b></TBtn>
              <TBtn testid="draw-italic" title="Italic" active={!!one.italic} onClick={() => act.update({ italic: !one.italic })}><i>I</i></TBtn>
              <ColorField testid="draw-textcolor" label="Text color" value={one.color} onChange={(v) => act.update({ color: v })} swatches={swatches} />
              <select data-testid="draw-text-align" value={one.align} onChange={(e) => act.update({ align: e.target.value })}
                className="h-8 rounded-md bg-slate-800/80 border border-slate-600 px-1.5 text-xs text-white outline-none">
                <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
              </select>
            </>
          )}
          {one.type === "image" && (
            <>
              <select data-testid="draw-image-pick" value={one.imageId || ""} onChange={(e) => act.update({ imageId: e.target.value })}
                className="h-8 rounded-md bg-slate-800/80 border border-slate-600 px-1.5 text-xs text-white outline-none">
                <option value="">Choose an image…</option>
                {(config.gallery || []).map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
              </select>
              <input data-testid="draw-image-alt" value={one.alt || ""} onChange={(e) => act.update({ alt: e.target.value })} placeholder="Alt text (describe the image)"
                className="h-8 w-52 rounded-md bg-slate-800/80 border border-slate-600 px-2 text-xs text-white placeholder:text-slate-400 outline-none focus:border-[#22D3EE]" />
            </>
          )}
          {one.type === "video" && (
            <select data-testid="draw-video-pick" value={one.videoId || ""} onChange={(e) => act.update({ videoId: e.target.value })}
              className="h-8 rounded-md bg-slate-800/80 border border-slate-600 px-1.5 text-xs text-white outline-none">
              <option value="">Choose a video…</option>
              {(config.videos || []).map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>
          )}
        </div>
      )}
    </div>
  );
}
